<template>
  <div class="app-promo">
    <!-- 纯色背景 -->
    <div class="solid-background"></div>

    <!-- 顶部导航栏 - 改为 div -->
    <div 
      class="navbar fixed-nav" 
      :class="{ 
        'navbar-hidden': !showNavbar
      }"
      ref="navbarRef"
    >
      <div class="logo-area">
        <img src="@/assets/logo.svg" alt="Logo" class="logo-img">
        <span>Accessible Translate</span>
      </div>
      <div class="actions">
        <div class="google-play-btn" @click="handleGooglePlayDownload">
          <span>
            <span class="btn-small-label">GET IT ON</span>
            <span class="btn-store-name">Google Play</span>
          </span>
        </div>
      </div>
    </div>

    <!-- 可滚动的内容区域 - 改为 div -->
    <div 
      class="scrollable-content" 
      ref="scrollableContentRef"
      @scroll="handleScroll"
    >
      <div class="video-list">
        <div 
          v-for="(video, index) in videos" 
          :key="index" 
          class="video-item"
        >
          <div class="video-info">
            <!-- Screen Translate 作为标题 -->
            <h3 class="video-title">{{ video.caption }}</h3>
            <div class="video-meta"></div>
          </div>
          <div class="video-container">
            <video 
              autoplay 
              muted 
              loop 
              playsinline 
              disablePictureInPicture
              class="rounded-video"
            >
              <source :src="video.src" type="video/mp4">
            </video>
          </div>
        </div>
      </div>

      <!-- ===== 新增：视频下方的问答区域（无图标版本） ===== -->
      <div class="qa-section">
        <h3 class="qa-heading">  FAQ  </h3>
        
        <div class="qa-list">
          <!-- 问答项 1 -->
          <div class="qa-item">
            <div class="qa-question">Can I use it for free?</div>
            <div class="qa-answer">
              Basic translation is permanently free, AI translation is available for a limited-time experience
            </div>
          </div>
          
          <!-- 问答项 2 -->
          <div class="qa-item">
            <div class="qa-question">How many languages are supported?</div>
            <div class="qa-answer">
                Currently supports mutual translation of over 100 languages, including Chinese, English, Spanish, Arabic, and more.
            </div>
          </div>
          
          <!-- 问答项 3 -->
          <div class="qa-item">
            <div class="qa-question">Which apps are supported?</div>
            <div class="qa-answer">
                    Supports more than 10 apps such as YouTube, X (Twitter), Reddit, Bilibili, and more will be added in the future.
            </div>
          </div>
          
          <!-- 问答项 4 -->
         
        </div>
        
        <!-- 更多帮助链接 -->
        <div class="qa-footer">
  <a href="#" class="more-link" @click.prevent="goToFaq">View all</a>
</div>
      </div>
      <!-- ===== 问答区域结束 ===== -->

      <!-- 页脚 - 改为 div -->
      <div class="app-footer">
        <p>© 2026 Accessible Translate</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
const videos = ref([
  {
    src: '/videos/freecompress1.mp4',
    caption: 'Screen Translate'
  },
  {
    src: '/videos/freecompress2.mp4',
    caption: 'Word By Word Translate'
  },
  {
    src: '/videos/freecompress3.mp4',
    caption: 'Input Box Translate'
  }
])
const router = useRouter()

const goToFaq = () => {
  router.push('/help') // 跳转到常见问题页面
}
// 导航栏显示状态控制
const showNavbar = ref(true)
const lastScrollTop = ref(0)
const scrollThreshold = 5
const scrollableContentRef = ref(null)
const navbarRef = ref(null)
const navbarHeight = ref(80)

// 滚动处理函数
const handleScroll = () => {
  if (!scrollableContentRef.value) return
  
  const currentScrollTop = scrollableContentRef.value.scrollTop
  const scrollingDown = currentScrollTop > lastScrollTop.value
  const scrollDifference = Math.abs(currentScrollTop - lastScrollTop.value)
  
  // 添加弹性区域判断，避免在顶部附近频繁切换
  const isNearTop = currentScrollTop < 20
  
  if (scrollDifference > scrollThreshold && !isNearTop) {
    if (scrollingDown && currentScrollTop > 20) {
      // 向下滚动，隐藏导航栏
      if (showNavbar.value) {
        showNavbar.value = false
      }
    } else if (!scrollingDown) {
      // 向上滚动，显示导航栏
      if (!showNavbar.value) {
        showNavbar.value = true
      }
    }
  }
  
  // 如果在顶部，强制显示导航栏
  if (currentScrollTop <= 5 && !showNavbar.value) {
    showNavbar.value = true
  }
  
  lastScrollTop.value = currentScrollTop
}

// 节流函数
const throttle = (func, delay) => {
  let lastCall = 0
  return function(...args) {
    const now = Date.now()
    if (now - lastCall >= delay) {
      func.apply(this, args)
      lastCall = now
    }
  }
}

// Google Play 下载处理
const handleGooglePlayDownload = () => {
  window.open('https://play.google.com/store/apps/details?id=com.floating.manager1', '_blank')
}

// 更新导航栏高度
const updateNavbarHeight = () => {
  if (navbarRef.value) {
    const height = navbarRef.value.offsetHeight
    navbarHeight.value = height
    document.documentElement.style.setProperty('--navbar-height', height + 'px')
  }
}

onMounted(() => {
  // 初始化导航栏高度
  updateNavbarHeight()
  
  // 监听滚动
  const throttledHandleScroll = throttle(handleScroll, 50)
  if (scrollableContentRef.value) {
    scrollableContentRef.value.addEventListener('scroll', throttledHandleScroll, { passive: true })
  }
  
  // 监听窗口大小变化
  window.addEventListener('resize', updateNavbarHeight)
  
  // 使用 MutationObserver 监听导航栏内容变化
  const observer = new MutationObserver(updateNavbarHeight)
  if (navbarRef.value) {
    observer.observe(navbarRef.value, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    })
  }
  
  return () => {
    if (scrollableContentRef.value) {
      scrollableContentRef.value.removeEventListener('scroll', throttledHandleScroll)
    }
    window.removeEventListener('resize', updateNavbarHeight)
    observer.disconnect()
  }
})
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  background-color: #f8f9fa;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  width: 100%;
  height: 100%;
  display: block;
}

:root {
  --navbar-height: 80px;
  --cyan-primary: #7ad663;      /* 青色主色调 - 匹配您的绿色主题 */
  --cyan-dark: #479215;          /* 深绿色 */
  --cyan-gradient: linear-gradient(90deg, #7ad663, #479215);
}
</style>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.app-promo {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  position: relative;
  background: #f8f9fa;
  color: #1e293b;
  overflow: hidden;
}

/* 纯色背景层 */
.solid-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: #e2ffbb;
  z-index: 0;
}

/* 顶部导航栏 - 原先是 header，现在是 div */
.fixed-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;
  transition: transform 0.3s cubic-bezier(0.2, 0, 0, 1);
  will-change: transform;
}

.video-info {
  width: 100%;
  display: grid;
  place-items: center;  /* 一键居中 */
}

.video-function {
  font-size: 1.1rem;      /* 大字 */
  font-weight: 700;
  color: #0f172a;         /* 更深色 */
  line-height: 1.3;
}

.video-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #03170a;
  line-height: 1.3;
  position: relative;  /* 关键：让伪元素相对于它定位 */
  display: inline-block; /* 让宽度自适应内容，下划线只在文字下方 */
}

/* 青色下划线 */
.video-title::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;              /* 从中间开始 */
  transform: translateX(-50%); /* 向左移动自身宽度的一半 */
  width: 100px;
  height: 3px;
  background: linear-gradient(90deg, #7ad663, #479215);
  border-radius: 3px;
}

.video-meta {
  font-size: 0.85rem;
  color: #648b67;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

/* 导航栏隐藏状态 */
.navbar-hidden {
  transform: translateY(-100%);
}

.logo-area {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 1.3rem;
  letter-spacing: -0.5px;
  color: #1e293b;
}

.logo-img {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: cover;
  display: inline-block;
}

.actions {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.google-play-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #485e38, #608447);
  border-radius: 40px;
  padding: 0.4rem 1rem 0.4rem 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 18px -8px rgba(37, 99, 235, 0.3);
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  font-weight: 600;
  color: white;
  white-space: nowrap;
}

.google-play-btn:active {
  transform: scale(0.98);
}

.google-play-btn span {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
}

.btn-small-label {
  font-size: 0.55rem;
  opacity: 0.9;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
}

.btn-store-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: white;
}

/* 可滚动的内容区域 - 原先是 main，现在是 div */
.scrollable-content {
  overflow-y: auto;
  overflow-x: hidden;
  background: transparent;
  -webkit-overflow-scrolling: touch;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: #f9fef2;
  z-index: 0;
  padding-top: var(--navbar-height);
}

/* 内容区域偏移状态 - 当导航栏显示时 */
.scrollable-content.content-shifted {
  padding-top: var(--navbar-height);
}

/* 视频列表容器 */
.video-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px 0 30px 0;
  gap: 30px;
}

.video-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.video-container {
  width: 90%;
  max-width: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  border-radius: 20px;
  background-color: #eaf0e2;
  box-shadow: 0 10px 30px rgba(103, 219, 31, 0.15);
}

.rounded-video {
  width: 100%;
  height: auto;
  aspect-ratio: 720 / 1572;
  object-fit: cover;
  border-radius: 16px;
  border: 2px solid rgba(103, 219, 31, 0.15);
  background-color: #e2e8f0;
  display: block;
}

.video-caption {
  margin-top: 12px;
  font-size: 1rem;
  font-weight: 500;
  color: #334155;
  text-align: center;
  width: 90%;
  max-width: 400px;
}

/* ===== 问答区域样式（无图标版本） ===== */

.qa-section {
  width: 100%;
  display: grid;
  place-items: center;  /* 一键居中 */
}
.qa-heading {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  position: relative;
  display: inline-block;
}

/* 绿色下划线装饰 - 匹配您的主题 */
.qa-heading::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 0;
  width: 40px;
  height: 3px;
  background: linear-gradient(90deg, #7ad663, #479215);
  border-radius: 3px;
}

.qa-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.qa-item {
  background: white;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.02), 0 0 0 1px rgba(122, 214, 99, 0.2);
  transition: all 0.2s ease;
}

.qa-item:hover {
  box-shadow: 0 12px 28px rgba(122, 214, 99, 0.15), 0 0 0 1px rgba(71, 146, 21, 0.3);
}

.qa-question {
  font-weight: 700;
  color: #0f172a;
  font-size: 1.1rem;
  margin-bottom: 12px;
  line-height: 1.4;
}

.qa-answer {
  color: #475569;
  font-size: 0.95rem;
  line-height: 1.4;
  text-align: left; /* 添加这一行，让内部文字左对齐 */
   text-indent: 1.2em; /* 首行缩进2个字符，最常用 */
}

.qa-footer {
  margin-top: 30px;
  text-align: center;
}

.more-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #479215;
  font-weight: 600;
  text-decoration: none;
  padding: 10px 24px;
  border-radius: 40px;
  background: rgba(122, 214, 99, 0.08);
  transition: all 0.2s;
}

.more-link:hover {
  background: rgba(122, 214, 99, 0.15);
}

/* 移动端优化 - 问答区域 */
@media (max-width: 480px) {
  .qa-section {
    padding: 0 16px;
    margin: 15px auto 25px;
  }
  
  .qa-heading {
    font-size: 1.3rem;
    margin-bottom: 20px;
  }
  
  .qa-heading::after {
    width: 50px;
    bottom: -6px;
  }
  
  .qa-question {
    font-size: 1rem;
    margin-bottom: 10px;
  }
  
  .qa-answer {
    font-size: 0.9rem;
  }
  
  .qa-item {
    padding: 16px;
  }
}

@media (max-width: 360px) {
  .qa-question {
    font-size: 0.95rem;
  }
  
  .qa-answer {
    font-size: 0.85rem;
  }
}
/* ===== 问答区域结束 ===== */

/* 页脚 - 原先是 footer，现在是 div */
.app-footer {
  text-align: center;
  padding: 1.5rem 1rem 2.5rem 1rem;
  font-size: 0.75rem;
  opacity: 0.6;
  width: 100%;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  color: #64748b;
  margin-top: 10px;
}

/* 移动端优化 */
@media (max-width: 480px) {
  .navbar {
    padding: 0.7rem 1rem;
  }
  
  .logo-area span {
    font-size: 1.1rem;
  }
  
  .logo-img {
    width: 20px;
    height: 20px;
  }
  
  .actions {
    gap: 0.5rem;
  }
  
  .google-play-btn {
    padding: 0.3rem 0.8rem 0.3rem 0.6rem;
  }
  
  .btn-store-name {
    font-size: 0.8rem;
  }
  
  .video-list {
    gap: 25px;
    padding: 15px 0 20px 0;
  }
  
  .video-container {
    width: 92%;
  }
  
  .video-caption {
    font-size: 0.9rem;
    margin-top: 8px;
  }
  
  .app-footer {
    padding: 1rem 0.5rem 2rem 0.5rem;
  }
}

@media (max-width: 360px) {
  .navbar {
    padding: 0.6rem 0.8rem;
  }
  
  .logo-area span {
    font-size: 1rem;
  }
  
  .logo-img {
    width: 18px;
    height: 18px;
  }
  
  .video-list {
    gap: 20px;
  }
  
  .video-container {
    width: 95%;
  }
}

button, 
.google-play-btn {
  min-height: 44px;
}

/* 安全区域适配 */
@supports (padding: max(0px)) {
  .navbar {
    padding-left: max(1.5rem, env(safe-area-inset-left));
    padding-right: max(1.5rem, env(safe-area-inset-right));
    padding-top: max(1rem, env(safe-area-inset-top));
  }
  
  .scrollable-content {
    padding-left: max(1rem, env(safe-area-inset-left));
    padding-right: max(1rem, env(safe-area-inset-right));
  }
  
  .app-footer {
    padding-bottom: max(2rem, env(safe-area-inset-bottom));
  }
}

.scrollable-content::-webkit-scrollbar {
  width: 4px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: transparent;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}
</style>